export type AiExample =
  | "normal"
  | "empty"
  | "invalid"
  | "readonly"
  | "no-model"
  | "disabled"
  | "error"
  | "local"
  | "old-bridge"
  | "unselected-models"
  | "invalid-default"
  | "restricted-models";
export type AiMode = "local" | "relay";
export type AiModels = {
  billing?: "api" | "plan";
  default: string;
  allowed: string[];
  prices?: Record<string, ModelPrice | null>;
};
export const aiModelsFixture: AiModels = {
  default: "gpt-5",
  allowed: ["gpt-5", "gpt-5-mini"],
  // Synthetic design prices; the live adapter supplies its catalog values.
  prices: {
    "gpt-5": { input: 1, cachedInput: 0.1, output: 5 },
    "gpt-5-mini": { input: 0.25, cachedInput: 0.025, output: 2 },
  },
};
export type ModelPrice = { input: number; cachedInput: number; output: number };
export type RelaySettings = {
  enabled: boolean;
  model: string;
  effort: string;
  allowedModels: string[];
  prices: Record<string, ModelPrice | null>;
  priceSource?: Record<
    string,
    { source: "auto" | "manual"; checkedAt: string | null }
  >;
};
export type RelayReadiness = {
  ok: boolean;
  reason: string;
  model: string;
  effort: string;
};
export type AiKey = {
  id: string;
  label: string;
  hint: string;
  status: "active" | "disabled" | "invalid";
  priority: number;
  monthlyBudgetUsd: number | null;
  month: { costUsd: number; calls: number; tokens: number };
  lastUsedAt: string | null;
  lastCheckedAt?: string | null;
  lastError: string | null;
  models: string[];
  modelsSource: "provider" | "builtin" | null;
  enabledModels: string[];
};
export type AiKeyInput = {
  label: string;
  secret: string;
  priority: number;
  monthlyBudgetUsd: number | null;
};
export type AiKeyPatch = Partial<
  Pick<AiKey, "label" | "priority" | "monthlyBudgetUsd" | "enabledModels">
> & { status?: "active" | "disabled" };
export type AiKeysView = {
  keys: AiKey[];
  models: AiModels & { defaultValid: boolean };
  relay: RelayReadiness;
  settings: RelaySettings;
};
/** Actions own API calls; refresh re-reads keys/settings and updates the supplied view. */
export type AiKeysActions = {
  create(input: AiKeyInput): Promise<{ key: AiKey; note?: string }>;
  patch(id: string, input: AiKeyPatch): Promise<void>;
  test(
    id: string,
  ): Promise<{ ok: boolean; key: AiKey; error?: string; note?: string }>;
  remove(id: string): Promise<void>;
  saveDefault(input: { model: string; effort: string }): Promise<void>;
  setRelayEnabled(enabled: boolean): Promise<void>;
  syncPrices(): Promise<void>;
  refresh(): Promise<void>;
};
const providerModels = ["gpt-5", "gpt-5-mini", "gpt-5-nano", "gpt-4.1"];
const builtinModels = ["gpt-6.1-sol", "gpt-6-astra", "gpt-6-sol", "gpt-6-luna"];
/** Fixtures alone derive readiness. The live screen displays the adapter's values. */
export function withFixtureAiState(view: AiKeysView): AiKeysView {
  const allowed = [
    ...new Set(
      view.keys
        .filter((k) => k.status === "active")
        .flatMap((k) => k.enabledModels),
    ),
  ];
  const settings = { ...view.settings, allowedModels: allowed };
  return {
    ...view,
    settings,
    models: {
      default: settings.model,
      allowed,
      defaultValid: allowed.includes(settings.model),
    },
    relay: fixtureReadiness(view.keys, settings),
  };
}
export function aiKeysFixture(example: AiExample = "normal"): AiKeysView {
  const restricted = example === "restricted-models";
  const models = restricted ? builtinModels : providerModels;
  const selected =
    example === "unselected-models"
      ? []
      : example === "invalid-default"
        ? ["gpt-5-mini"]
        : models.slice(0, 2);
  const settings: RelaySettings = {
    enabled: example !== "disabled",
    model: example === "no-model" || example === "empty" ? "" : models[0],
    effort: "medium",
    allowedModels: [],
    prices: {
      [models[0]]: { input: 1, cachedInput: 0.1, output: 5 },
      [models[1]]: { input: 0.25, cachedInput: 0.025, output: 2 },
    },
    priceSource: {
      [models[0]]: { source: "auto", checkedAt: "2026-10-21 09:00" },
      [models[1]]: { source: "manual", checkedAt: "2026-10-20 16:40" },
    },
  };
  const keys: AiKey[] =
    example === "empty"
      ? []
      : [
          {
            id: "key-main",
            label: "운영 기본 키",
            hint: "sk-proj…7Y2A",
            status: example === "invalid" ? "invalid" : "active",
            priority: 10,
            monthlyBudgetUsd: 200,
            month: { costUsd: 84.62, calls: 1284, tokens: 2841600 },
            lastUsedAt: "2026-10-21 14:32",
            lastCheckedAt: "2026-10-21 09:00",
            lastError:
              example === "invalid" ? "HTTP 401 · 인증에 실패했습니다" : null,
            models,
            modelsSource: restricted ? "builtin" : "provider",
            enabledModels: selected,
          },
          {
            id: "key-backup",
            label: "보조 키",
            hint: "sk-proj…9K4B",
            status: "disabled",
            priority: 20,
            monthlyBudgetUsd: 100,
            month: { costUsd: 6.18, calls: 93, tokens: 196340 },
            lastUsedAt: "2026-10-20 18:20",
            lastCheckedAt: "2026-10-20 16:40",
            lastError: null,
            models: providerModels,
            modelsSource: "provider",
            enabledModels:
              example === "unselected-models" ? [] : ["gpt-5-mini"],
          },
        ];
  return withFixtureAiState({
    keys,
    settings,
    models: { default: settings.model, allowed: [], defaultValid: false },
    relay: {
      ok: false,
      reason: "",
      model: settings.model,
      effort: settings.effort,
    },
  });
}
export function fixtureReadiness(
  keys: AiKey[],
  s: RelaySettings,
): RelayReadiness {
  const reason = !keys.length
    ? "먼저 API 키를 등록하세요"
    : !s.enabled
      ? "API 키 방식이 꺼져 있습니다"
      : !keys.some((k) => k.status === "active")
        ? "쓸 수 있는 키가 없습니다. 키의 연결 상태를 확인하세요."
        : !s.allowedModels.length
          ? "키마다 쓸 모델을 고르세요"
          : !s.model
            ? "기본 모델을 고르세요"
            : !s.allowedModels.includes(s.model)
              ? "기본 모델이 고른 모델에 없습니다. 다시 고르세요."
              : "사용 가능";
  return {
    ok: reason === "사용 가능",
    reason,
    model: s.model,
    effort: s.effort,
  };
}
export type AiCall = {
  id: string;
  taskId: string | null;
  runId: string | null;
  taskTitle: string | null;
  customerId: string;
  customerName: string;
  targetCustomerName: string | null;
  machineId: string;
  machineLabel: string;
  deviceSerial: string | null;
  mode: AiMode;
  model: string;
  keyLabel: string | null;
  keyHint: string | null;
  status: "ok" | "error" | "cancelled";
  inputTokens: number;
  cachedTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  costUsd: number | null;
  firstByteMs: number | null;
  durationMs: number | null;
  startedAt: string;
  error: string | null;
};
export type AiFilters = {
  from: string;
  to: string;
  customerId: string;
  machineId: string;
  model: string;
  mode: string;
  status: string;
};
export type AiTotals = {
  calls: number;
  failed: number;
  inputTokens: number;
  cachedTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  costUsd: number;
  unpriced: number;
};
export type AiGroup = AiTotals & { key: string };
export type AiUsageView = {
  totals: AiTotals;
  days: { day: string; local: number; relay: number }[];
  groups: Record<"customer" | "machine" | "model" | "key", AiGroup[]>;
  calls: AiCall[];
  total: number;
  customers: { id: string; label: string }[];
  machines: { id: string; label: string }[];
  models: string[];
};
export const initialAiFilters: AiFilters = {
  from: "2026-10-14",
  to: "2026-10-20",
  customerId: "all",
  machineId: "all",
  model: "all",
  mode: "all",
  status: "all",
};
export const aiCallsFixture: AiCall[] = Array.from({ length: 18 }, (_, i) => ({
  id: "call-" + i,
  taskId: i % 3 === 0 ? "JOB-204" : "JOB-205",
  runId: i % 3 === 0 ? "RUN-1048" : "RUN-1047",
  taskTitle: i % 3 === 0 ? "건강검진 안내 발행" : "가을 진료 안내",
  customerId: i % 3 === 0 ? "moru" : "onyou",
  customerName: i % 3 === 0 ? "모루 운영팀" : "온유의원",
  targetCustomerName: i % 3 === 0 ? "온유의원" : null,
  machineId: i % 3 === 0 ? "machine-crew" : "machine-main",
  machineLabel: i % 3 === 0 ? "크루 운영 PC" : "서울 접수 PC",
  deviceSerial: i % 3 === 0 ? "R5-DEMO-02" : "R5-DEMO-01",
  mode: i % 4 === 0 ? "local" : "relay",
  model: i === 4 ? "단가 미등록 모델" : i % 3 === 0 ? "gpt-5-mini" : "gpt-5",
  keyLabel: i % 4 === 0 ? null : "운영 기본 키",
  keyHint: i % 4 === 0 ? null : "sk-proj…7Y2A",
  status: i === 2 ? "error" : i === 7 ? "cancelled" : "ok",
  inputTokens: 24000 + i * 1800,
  cachedTokens: 12000 + i * 500,
  outputTokens: 1300 + i * 200,
  reasoningTokens: 600 + i * 70,
  costUsd: i === 4 ? null : 0.024 + i * 0.006,
  firstByteMs: i % 4 === 0 ? null : 780 + i * 32,
  durationMs: 8500 + i * 900,
  startedAt: `2026-10-${20 - Math.floor(i / 3)}T${String(14 - (i % 3)).padStart(2, "0")}:32:00+09:00`,
  error: i === 2 ? "HTTP 429 · 사용 가능한 키의 한도를 초과했습니다" : null,
}));
function totals(rows: AiCall[]): AiTotals {
  return rows.reduce(
    (s, r) => ({
      calls: s.calls + 1,
      failed: s.failed + (r.status !== "ok" ? 1 : 0),
      inputTokens: s.inputTokens + r.inputTokens,
      cachedTokens: s.cachedTokens + r.cachedTokens,
      outputTokens: s.outputTokens + r.outputTokens,
      reasoningTokens: s.reasoningTokens + r.reasoningTokens,
      costUsd: s.costUsd + (r.costUsd ?? 0),
      unpriced: s.unpriced + (r.costUsd === null && r.status === "ok" ? 1 : 0),
    }),
    {
      calls: 0,
      failed: 0,
      inputTokens: 0,
      cachedTokens: 0,
      outputTokens: 0,
      reasoningTokens: 0,
      costUsd: 0,
      unpriced: 0,
    },
  );
}
export function aiUsageFixture(
  filters = initialAiFilters,
  empty = false,
): AiUsageView {
  const rows = empty
    ? []
    : aiCallsFixture.filter(
        (r) =>
          r.startedAt.slice(0, 10) >= filters.from &&
          r.startedAt.slice(0, 10) <= filters.to &&
          (filters.customerId === "all" ||
            r.customerId === filters.customerId ||
            (filters.customerId === "onyou" &&
              r.targetCustomerName === "온유의원")) &&
          (filters.machineId === "all" || r.machineId === filters.machineId) &&
          (filters.model === "all" || r.model === filters.model) &&
          (filters.mode === "all" || r.mode === filters.mode) &&
          (filters.status === "all" || r.status === filters.status),
      );
  const group = (fn: (r: AiCall) => string) =>
    [...new Set(rows.map(fn))].map((key) => ({
      key,
      ...totals(rows.filter((r) => fn(r) === key)),
    }));
  const days = [...new Set(rows.map((r) => r.startedAt.slice(0, 10)))]
    .sort()
    .map((day) => ({
      day,
      local: rows.filter(
        (r) => r.startedAt.startsWith(day) && r.mode === "local",
      ).length,
      relay: rows.filter(
        (r) => r.startedAt.startsWith(day) && r.mode === "relay",
      ).length,
    }));
  return {
    totals: totals(rows),
    days,
    groups: {
      customer: group((r) => r.customerName),
      machine: group((r) => r.machineLabel),
      model: group((r) => r.model),
      key: group((r) => r.keyLabel ?? "로컬 세션"),
    },
    calls: rows,
    total: rows.length,
    customers: [
      { id: "onyou", label: "온유의원" },
      { id: "moru", label: "모루 운영팀" },
    ],
    machines: [
      { id: "machine-main", label: "서울 접수 PC" },
      { id: "machine-crew", label: "크루 운영 PC" },
    ],
    models: [...new Set(aiCallsFixture.map((r) => r.model))],
  };
}
export type MachineAiView = {
  mode: AiMode | null;
  defaultMode: AiMode;
  loggedIn: boolean;
  bridgeReady: boolean;
  readiness: RelayReadiness;
};
export function machineAiFixture(example: AiExample = "normal"): MachineAiView {
  return {
    mode: example === "local" ? "local" : null,
    defaultMode: "relay",
    loggedIn: example !== "local",
    bridgeReady: example !== "old-bridge",
    readiness: aiKeysFixture(example).relay,
  };
}
